function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import Decimal from 'decimal.js';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Button, ConfirmationModal, CustomModal, FullPageModal, Typography, normalizeDescription, useOpenUnsavedChangesModal, useOverlayContext } from 'seagull';
import IncludedServices from "../../IncludedServices/IncludedServices";
import ServiceAccountingField from "./ServiceAccountingField";
import { useStore, useStoreActions } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { handlePromise } from "../../../infra/utils";
import { getPMDetails } from "../../../integrations/PracticeManagement/pm.utils";
import PMMapServiceField from "../../../integrations/PracticeManagement/PMMapServiceField/PMMapServiceField";
import { DiscountType } from "../../../models";
import { ServiceTemplateUsedIn } from "../../../models/service-template/model";
import { selectors } from "../../../store/state-selectors";
import { useAppConfig } from "../../../store/store-context";
import { Accounting, PracticeManagement } from "../../../types/api.model";
import { filterServiceOnlyTemplates } from "../../../utils/filterServiceOnlyTemplates";
import { getHelperText, isValueTypeDollars } from "./BillingStagesSelect/BillingStagesSelect.utils";
import BillingTriggerDropdown from "./BillingTriggerDropdown";
import DiscountInput from "./DiscountInput";
import HelpMeWriteDrawer from "./HelpMeWriteDrawer/HelpMeWriteDrawer";
import MinPriceTextField from "./MinPriceTextField";
import OccurrenceTextField from "./OccurrenceTextField/OccurrenceTextField";
import PreapprovedAmountSection from "./PreapprovedAmountSection";
import PreapprovedTextField from "./PreapprovedTextField";
import PriceTextField from "./PriceTextField";
import PricingTextField from "./PricingTextField";
import styles from "./ServiceBuilder.module.scss";
import { getDefaultServiceMapping, getPriceAfterDiscount, isNameFieldDisabled, outputToService, quillToText, serviceToInput } from "./ServiceBuilder.utils";
import ServiceDescription from "./ServiceDescription";
import ServiceNameTextField from "./ServiceNameTextField";
import UnitNameTextField from "./UnitNameTextField";
import UpdateExistingServicesContent from "./UpdateExistingServicesContent";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const formId = 'service-builder-form';
const rootTestid = 'service-builder';
export const defaultInitialState = {
  price: '',
  pricing: 'fixed',
  trigger: 'approval',
  unitCap: '',
  minPrice: '',
  unitName: '',
  discount: {
    type: DiscountType.Percent,
    amount: ''
  },
  serviceName: '',
  description: '',
  karbonMapping: [],
  keeperMapping: [],
  clientHubMapping: [],
  recurrencePeriod: 'onetime',
  serviceQboMapping: {
    type: 'unmapped'
  },
  serviceXeroMapping: {
    type: 'unmapped'
  },
  financialCentsMapping: [],
  oneTimeInStagesBilling: null
};
export default function ServiceBuilder(props) {
  var _service$id;
  const {
    service,
    agreementData,
    serviceOrigin,
    serviceUsedIn,
    serviceNameBlacklist,
    applyOutputToChanges,
    isCreatingBundle,
    onSave,
    dismissModal,
    registerCanDismiss
  } = props;
  const {
    fetchAccountingItems,
    fetchIntegrationsSummary,
    fetchKarbonItems,
    fetchClientHubItems,
    fetchFinancialCentsItems,
    fetchKeeperItems,
    logBI
  } = useStoreActions();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const disableIntegrationsMapping = !!service && serviceOrigin !== 'template';
  const fullPageModal = !service || serviceOrigin === 'template';
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
  const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
  const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
  const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
  const qboItems = useStore(selectors.integration.qboItems);
  const financialCentsItems = useStore(selectors.integration.financialCentsItems);
  const karbonItems = useStore(selectors.integration.karbonItems);
  const clientHubItems = useStore(selectors.integration.clientHubItems);
  const keeperItems = useStore(selectors.integration.keeperItems);
  const xeroItems = useStore(selectors.integration.xeroItems);
  const {
    XERO_INTEGRATION,
    PREAPPROVED_AMOUNT_IMPROVMENTS,
    SERVICE_TEMPLATE_BUNDLE,
    SERVICE_DESCRIPTION_AI,
    HEADER_LAYOUT_UPDATES
  } = useFeatureFlags();
  const {
    generateUniqueId
  } = useAppConfig();
  const [saving, setSaving] = useState(false);
  const [isHelpMeWriteDrawerOpen, setIsHelpMeWriteDrawerOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(564);
  const overlayContext = useOverlayContext();
  const rightSectionRef = useRef(null);
  const defaultValues = useMemo(() => service ? serviceToInput({
    service,
    qboItems: (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || [],
    xeroItems: (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || []
  }) : _objectSpread(_objectSpread({}, defaultInitialState), {}, {
    bundle: SERVICE_TEMPLATE_BUNDLE && isCreatingBundle ? [] : undefined
  }), [service, qboItems === null || qboItems === void 0 ? void 0 : qboItems.items, xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items, SERVICE_TEMPLATE_BUNDLE, isCreatingBundle]);
  const isBundle = Boolean(SERVICE_TEMPLATE_BUNDLE && defaultValues.bundle);
  const showPracticeManagementIntegration = shouldShow => shouldShow && !isBundle;
  const headerText = "".concat(service ? 'Edit' : 'Create', " ").concat(isBundle ? 'Bundle' : 'Service');
  const onClose = () => dismissModal();
  const form = useForm({
    mode: 'all',
    defaultValues
  });
  const isFormDirty = form.formState.isDirty;
  const [pricing, price, minPrice, discount, oneTimeInStagesBilling, description, serviceName] = form.watch(['pricing', 'price', 'minPrice', 'discount', 'oneTimeInStagesBilling', 'description', 'serviceName']);
  const hasDiscountError = Boolean(form.formState.errors.discount);
  const isDescriptionDirty = !!form.formState.dirtyFields.description && quillToText(description) !== quillToText(defaultValues.description);
  useEffect(() => {
    if (oneTimeInStagesBilling && form.getValues().discount.type === DiscountType.Value) form.setValue('discount', {
      type: DiscountType.Value,
      amount: ''
    });
  }, [oneTimeInStagesBilling]);
  useEffect(() => {
    const serviceQboMapping = getDefaultServiceMapping(service === null || service === void 0 ? void 0 : service.integrations.qboIntegratedId(), (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []);
    const serviceXeroMapping = getDefaultServiceMapping(service === null || service === void 0 ? void 0 : service.integrations.xeroIntegratedId(), (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || []);
    form.setValue('serviceQboMapping', serviceQboMapping);
    form.setValue('serviceXeroMapping', serviceXeroMapping);
  }, [qboItems === null || qboItems === void 0 ? void 0 : qboItems.items, service === null || service === void 0 ? void 0 : service.integrations, xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items]);
  useEffect(() => {
    if (showQboIntegration || showFinancialCentsIntegration || showKarbonIntegration || showClientHubIntegration) fetchIntegrationsSummary();
    if (showQboIntegration) fetchAccountingItems(Accounting.QBO, !!SERVICE_TEMPLATE_BUNDLE);
    if (showXeroIntegration) fetchAccountingItems(Accounting.Xero, !!SERVICE_TEMPLATE_BUNDLE);
    if (showFinancialCentsIntegration) handlePromise(fetchFinancialCentsItems());
    if (showKarbonIntegration) handlePromise(fetchKarbonItems());
    if (showClientHubIntegration) handlePromise(fetchClientHubItems());
    if (showKeeperIntegration) handlePromise(fetchKeeperItems());
  }, [showQboIntegration, showFinancialCentsIntegration, showKarbonIntegration, showClientHubIntegration, showKeeperIntegration, showXeroIntegration, SERVICE_TEMPLATE_BUNDLE]);
  useEffect(() => {
    if (!rightSectionRef.current) return;
    const updateDrawerWidth = () => {
      var _rightSectionRef$curr;
      const width = (_rightSectionRef$curr = rightSectionRef.current) === null || _rightSectionRef$curr === void 0 ? void 0 : _rightSectionRef$curr.getBoundingClientRect().width;
      if (width) {
        // 24px padding, 36px header gap, 36px footer gap, 12px margin of the drawer twice
        setDrawerWidth(width + 24 + 36 + 36 - 12 - 12);
      }
    };
    updateDrawerWidth();
    const resizeObserver = new ResizeObserver(updateDrawerWidth);
    resizeObserver.observe(rightSectionRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const discountedPrice = useMemo(() => getPriceAfterDiscount({
    pricing,
    price,
    minPrice,
    discount
  }), [pricing, price, minPrice, discount]);
  const updateExistingServicesForm = useForm({
    mode: 'all',
    defaultValues: {
      [ServiceTemplateUsedIn.Draft]: false,
      [ServiceTemplateUsedIn.ProposalTemplate]: false
    }
  });
  const onSubmit = async value => {
    if (!isFormDirty && !isDescriptionDirty) {
      // Do nothing if form hasn't changed.
      return onClose();
    }
    let descriptionObj = null;
    if (serviceUsedIn !== null && serviceUsedIn !== void 0 && serviceUsedIn.some(usedIn => [ServiceTemplateUsedIn.ProposalTemplate, ServiceTemplateUsedIn.Draft].includes(usedIn)) && isDescriptionDirty) {
      const confirmed = await overlayContext.showModal({
        closeOnClickOutside: true,
        content: _ref => {
          let {
            dismissModal
          } = _ref;
          return /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, updateExistingServicesForm), {}, {
            children: /*#__PURE__*/_jsx(ConfirmationModal, {
              width: "660px",
              "data-testid": "update-existing-services-modal",
              title: "Update existing services",
              textAlignCenter: false,
              content: /*#__PURE__*/_jsx(UpdateExistingServicesContent, {
                serviceUsedIn: serviceUsedIn
              }),
              primaryButtonLabel: "Save Service",
              primaryButtonTestid: "update-existing-services-modal-primary-btn",
              onPrimaryButtonClick: () => dismissModal(true),
              secondaryButtonLabel: "Cancel",
              onSecondaryButtonClick: () => {
                dismissModal(false);
              }
            })
          }));
        }
      });
      if (!confirmed) {
        updateExistingServicesForm.reset();
        return;
      }
      const {
        draft,
        proposalTemplate
      } = updateExistingServicesForm.getValues();
      const updateExistingServices = {
        [ServiceTemplateUsedIn.Draft]: draft,
        [ServiceTemplateUsedIn.ProposalTemplate]: proposalTemplate
      };
      descriptionObj = {
        content: value.description,
        updateRelationshipDrafts: updateExistingServices.draft,
        updateProposalTemplates: updateExistingServices.proposalTemplate
      };
    }
    const outputService = outputToService({
      output: value,
      agreementData,
      originalService: service,
      applyOutputToChanges,
      isAgreement: serviceOrigin === 'agreement',
      generateUniqueId
    });
    if (onSave) {
      setSaving(true);
      const result = await onSave(outputService, descriptionObj || undefined);
      setSaving(false);
      if (!result.success) return;
    }
    dismissModal(outputService, {
      force: true
    });
  };
  registerCanDismiss(async () => {
    if (!isFormDirty) return true;
    const confirmTitle = service ? 'Discard changes' : 'Cancel service';
    return openUnsavedChangesModal({
      testid: 'sb-confirm-cancel',
      title: confirmTitle,
      primaryButtonLabel: confirmTitle,
      content: 'Unsaved changes will be lost. To save the changes, complete the service details and click Save and exit.'
    });
  });
  const actionButtons = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Button, {
      skin: "primaryOutline",
      "data-testid": "sb-cancel",
      onClick: onClose,
      children: "Cancel"
    }), /*#__PURE__*/_jsx(Button, {
      type: "submit",
      "data-testid": "sb-submit",
      form: formId,
      loading: saving,
      children: "Save"
    })]
  });
  const showAiDescriptionPanel = SERVICE_DESCRIPTION_AI && !isBundle;
  const disableAiDescriptionPanel = showAiDescriptionPanel && form.watch('serviceName') === '';
  const onHelpMeWriteClick = showAiDescriptionPanel ? () => {
    logBI(17001);
    setIsHelpMeWriteDrawerOpen(prev => !prev);
  } : undefined;
  const content = /*#__PURE__*/_jsx(FormProvider, _objectSpread(_objectSpread({}, form), {}, {
    children: /*#__PURE__*/_jsxs("form", {
      id: formId,
      className: styles.content,
      noValidate: true,
      onSubmit: form.handleSubmit(onSubmit),
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.leftSection,
        children: [/*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(ServiceNameTextField, {
            testid: "sb-name",
            disabled: isHelpMeWriteDrawerOpen || isNameFieldDisabled(service, serviceOrigin),
            serviceNameBlacklist: serviceNameBlacklist
          })
        }), /*#__PURE__*/_jsx(ServiceDescription, {
          testId: "sb-description",
          onHelpMeWriteClick: onHelpMeWriteClick,
          disabledHelpMeWrite: disableAiDescriptionPanel,
          isDrawerOpen: isHelpMeWriteDrawerOpen,
          disabled: isHelpMeWriteDrawerOpen
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: styles.sep
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.rightSection,
        ref: rightSectionRef,
        children: [/*#__PURE__*/_jsxs("div", {
          className: cn(styles.topRightSection, {
            [styles.hasDiscountedPrice]: discountedPrice,
            [styles.isBundle]: isBundle
          }),
          children: [/*#__PURE__*/_jsx(OccurrenceTextField, {
            serviceOrigin: serviceOrigin,
            service: service
          }), /*#__PURE__*/_jsx(PricingTextField, {
            isBundle: isBundle,
            serviceOrigin: serviceOrigin,
            isExistingService: !!service
          }), pricing === 'unit' && /*#__PURE__*/_jsx(UnitNameTextField, {
            testid: "sb-unit-name"
          }), pricing === 'range' && /*#__PURE__*/_jsx(MinPriceTextField, {
            testid: "sb-min-price"
          }), !isBundle && /*#__PURE__*/_jsx(PriceTextField, {
            testid: "sb-price"
          }), !isBundle && /*#__PURE__*/_jsx(DiscountInput, {
            testid: "sb-discount"
          })]
        }), discountedPrice && !isBundle && !hasDiscountError && /*#__PURE__*/_jsxs(Typography, {
          className: styles.rowGap,
          "data-testid": "sb-discounted-price",
          children: ["Price after discount: ", discountedPrice]
        }), /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "oneTimeInStagesBilling",
            control: form.control,
            rules: {
              validate: () => {
                if (!oneTimeInStagesBilling) return true;
                const stages = oneTimeInStagesBilling.stages();
                const valueType = oneTimeInStagesBilling.valueType();
                const totalRate = new Decimal(price || 0);
                return !stages.some((stage, index) => {
                  const isLast = index === stages.length - 1;
                  const totalSumExceedsRate = stages.reduce((acc, stage) => acc.plus(stage.value), new Decimal(0)).gt(isValueTypeDollars(valueType) ? totalRate : new Decimal(100));
                  const helperText = getHelperText(stage, isLast, valueType, totalRate, totalSumExceedsRate);
                  return !!helperText;
                });
              }
            },
            render: () => /*#__PURE__*/_jsx(BillingTriggerDropdown, {
              service: service,
              effectiveDate: (agreementData === null || agreementData === void 0 ? void 0 : agreementData.effectiveDate) || null,
              serviceOrigin: serviceOrigin
            })
          })
        }), ['hour', 'unit'].includes(pricing) && (PREAPPROVED_AMOUNT_IMPROVMENTS ? /*#__PURE__*/_jsx(PreapprovedAmountSection, {}) : /*#__PURE__*/_jsx("div", {
          className: styles.preapprovedSection,
          children: /*#__PURE__*/_jsx(PreapprovedTextField, {})
        })), isBundle && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "bundle",
            control: form.control,
            render: _ref2 => {
              let {
                field: {
                  value,
                  onChange: _onChange
                }
              } = _ref2;
              return /*#__PURE__*/_jsx(IncludedServices, {
                tooltip: "Included service rates determine the bundle price. Rates are adjustable when adding the bundle to a proposal.",
                mode: "delete",
                value: value || [],
                onChange: value => {
                  _onChange(value);
                  const price = value.reduce((acc, service) => acc.plus(service.rate), new Decimal(0));
                  form.setValue('price', price.toString());
                }
              });
            }
          })
        }), showQboIntegration && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(ServiceAccountingField, {
            controlName: "serviceQboMapping",
            accounting: Accounting.QBO,
            accountingItems: isBundle ? (qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || [] : filterServiceOnlyTemplates((qboItems === null || qboItems === void 0 ? void 0 : qboItems.items) || []),
            isDisabled: disableIntegrationsMapping,
            testid: "qbo-mapping",
            isBundle: isBundle
          })
        }), showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(ServiceAccountingField, {
            controlName: "serviceXeroMapping",
            accounting: Accounting.Xero,
            accountingItems: (xeroItems === null || xeroItems === void 0 ? void 0 : xeroItems.items) || null,
            isDisabled: disableIntegrationsMapping,
            testid: "xero-mapping",
            isBundle: isBundle
          })
        }), showPracticeManagementIntegration(showFinancialCentsIntegration) && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "financialCentsMapping",
            control: form.control,
            render: _ref3 => {
              let {
                field: {
                  value,
                  onChange
                }
              } = _ref3;
              return /*#__PURE__*/_jsx(PMMapServiceField, {
                testid: "financial-cents-mapping",
                label: "".concat(getPMDetails(PracticeManagement.FinancialCents).name, " mapping"),
                options: financialCentsItems,
                loading: !financialCentsItems,
                disabled: disableIntegrationsMapping,
                tooltip: disableIntegrationsMapping ? "".concat(getPMDetails(PracticeManagement.FinancialCents).name, " ").concat(getPMDetails(PracticeManagement.FinancialCents).items, " can be mapped through the services library") : '',
                value: value,
                onChange: onChange
              });
            }
          })
        }), showPracticeManagementIntegration(showKarbonIntegration) && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "karbonMapping",
            control: form.control,
            render: _ref4 => {
              let {
                field: {
                  value,
                  onChange
                }
              } = _ref4;
              return /*#__PURE__*/_jsx(PMMapServiceField, {
                testid: "karbon-mapping",
                label: "".concat(getPMDetails(PracticeManagement.Karbon).name, " mapping"),
                options: karbonItems,
                loading: !karbonItems,
                disabled: disableIntegrationsMapping,
                tooltip: disableIntegrationsMapping ? "".concat(getPMDetails(PracticeManagement.Karbon).name, " ").concat(getPMDetails(PracticeManagement.Karbon).items, " can be mapped through the services library") : '',
                value: value,
                onChange: onChange
              });
            }
          })
        }), showPracticeManagementIntegration(showClientHubIntegration) && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "clientHubMapping",
            control: form.control,
            render: _ref5 => {
              let {
                field: {
                  value,
                  onChange
                }
              } = _ref5;
              return /*#__PURE__*/_jsx(PMMapServiceField, {
                testid: "client-hub-mapping",
                label: "".concat(getPMDetails(PracticeManagement.ClientHub).name, " mapping"),
                options: clientHubItems,
                loading: !clientHubItems,
                disabled: disableIntegrationsMapping,
                tooltip: disableIntegrationsMapping ? "".concat(getPMDetails(PracticeManagement.ClientHub).name, " ").concat(getPMDetails(PracticeManagement.ClientHub).items, " can be mapped through the services library") : '',
                value: value,
                onChange: onChange
              });
            }
          })
        }), showPracticeManagementIntegration(showKeeperIntegration) && /*#__PURE__*/_jsx("div", {
          className: styles.rowGap,
          children: /*#__PURE__*/_jsx(Controller, {
            name: "keeperMapping",
            control: form.control,
            render: _ref6 => {
              let {
                field: {
                  value,
                  onChange
                }
              } = _ref6;
              return /*#__PURE__*/_jsx(PMMapServiceField, {
                testid: "keeper-mapping",
                label: "".concat(getPMDetails(PracticeManagement.Keeper).name, " mapping"),
                options: keeperItems,
                loading: !keeperItems,
                disabled: disableIntegrationsMapping,
                tooltip: disableIntegrationsMapping ? "".concat(getPMDetails(PracticeManagement.Keeper).name, " ").concat(getPMDetails(PracticeManagement.Keeper).items, " can be mapped through the services library") : '',
                value: value,
                onChange: onChange
              });
            }
          })
        })]
      })]
    })
  }));
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [fullPageModal ? /*#__PURE__*/_jsxs(FullPageModal, {
      showFooter: !HEADER_LAYOUT_UPDATES,
      newUI: HEADER_LAYOUT_UPDATES,
      "data-testid": rootTestid,
      "data-fullpage": fullPageModal,
      contentSkin: "light",
      onClose: onClose,
      children: [/*#__PURE__*/_jsx(FullPageModal.HeaderStart, {
        children: headerText
      }), content, /*#__PURE__*/_jsx(FullPageModal.MainActions, {
        newUI: HEADER_LAYOUT_UPDATES,
        children: actionButtons
      })]
    }) : /*#__PURE__*/_jsx(CustomModal, {
      width: 1200,
      maxWidth: "90%",
      "data-testid": rootTestid,
      "data-fullpage": fullPageModal,
      header: /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        weight: "bold",
        children: headerText
      }),
      headerClassName: styles.modalHeader,
      footer: /*#__PURE__*/_jsxs("div", {
        className: styles.modalFooter,
        children: [/*#__PURE__*/_jsxs(Typography, {
          children: ["*Changes you make to this ", isBundle ? 'bundle' : 'service', " will apply only to this agreement"]
        }), /*#__PURE__*/_jsx("div", {
          className: styles.modalFooterEnd,
          children: actionButtons
        })]
      }),
      onClose: onClose,
      children: content
    }), SERVICE_DESCRIPTION_AI && /*#__PURE__*/_jsx(HelpMeWriteDrawer, {
      open: isHelpMeWriteDrawerOpen,
      onClose: () => setIsHelpMeWriteDrawerOpen(false),
      drawerWidth: drawerWidth,
      serviceName: serviceName,
      serviceDescription: description,
      serviceId: (_service$id = service === null || service === void 0 ? void 0 : service.id) !== null && _service$id !== void 0 ? _service$id : 'NA',
      onInsert: parsedRichText => {
        form.setValue('description', normalizeDescription(parsedRichText), {
          shouldDirty: true
        });
      }
    })]
  });
}